//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { trackMediaEvents } from '@apis/eventTracking/eventTrackingApi';
import { mockTrackMediaEvents } from '@apis/eventTracking/mockEventTrackingApi';
import {
  DEFAULT_MAX_MEDIA_EVENTS_QUEUE_SIZE,
  DEFAULT_MEDIA_EVENTS_QUEUE_TIMEOUT,
  MEDIA_TRACKING_DEBUG_LOGGING_ENABLED_MSG,
  MEDIA_TRACKING_MOCK_API_CALLS_ENABLED_MSG,
} from '@tracker/mediaTracking/constants/mediaTrackingConstants';
import {
  IMediaTrackingLogger,
  getMediaTrackingLogger,
} from '@tracker/mediaTracking/mediaTrackingLogger';
import {
  MediaTrackingEvent,
  MediaTrackingEventName,
  MediaTrackingQueueEvent,
} from '@tracker/mediaTracking/mediaTrackingModel';
import { isDefined } from '@utils/filterPredicates';

interface IDebugConfig {
  areMockApiCallsEnabled: boolean;
  isLoggingEnabled: boolean;
  eventsToLog: MediaTrackingEventName[];
}

interface IMediaTrackingQueueConfig {
  sessionId: string;
  isTrackingEnabled: boolean;
  maxEventsInQueue?: number;
  timeoutLength?: number;
  debugConfig: Partial<IDebugConfig>;
}

const defaultDebugConfig: IDebugConfig = {
  eventsToLog: [],
  isLoggingEnabled: false,
  areMockApiCallsEnabled: false,
};

export class MediaEventTrackingQueue {
  private eventsQueue: MediaTrackingEvent[] = [];
  private maxEventsInQueue = DEFAULT_MAX_MEDIA_EVENTS_QUEUE_SIZE;
  private timeoutLength = DEFAULT_MEDIA_EVENTS_QUEUE_TIMEOUT;
  private trackEvents = trackMediaEvents;
  private timeoutId: number | undefined = undefined;
  private sessionId: string | undefined;
  private isInitialized = false;
  private isTrackingEnabled = false;
  private eventsToLog: MediaTrackingEventName[] = [];
  private logger: IMediaTrackingLogger = getMediaTrackingLogger(false);

  public init({
    debugConfig,
    isTrackingEnabled,
    maxEventsInQueue,
    sessionId,
    timeoutLength,
  }: IMediaTrackingQueueConfig) {
    const { areMockApiCallsEnabled, eventsToLog, isLoggingEnabled } = {
      ...defaultDebugConfig,
      ...debugConfig,
    };

    this.logger = getMediaTrackingLogger(isLoggingEnabled);

    this.eventsToLog = eventsToLog;

    if (isDefined(maxEventsInQueue)) {
      this.maxEventsInQueue = maxEventsInQueue;
    }

    if (isDefined(timeoutLength)) {
      this.timeoutLength = timeoutLength;
    }

    if (areMockApiCallsEnabled) {
      this.logger.debug(MEDIA_TRACKING_MOCK_API_CALLS_ENABLED_MSG);
      this.trackEvents = mockTrackMediaEvents;
    }

    this.logger.debug(MEDIA_TRACKING_DEBUG_LOGGING_ENABLED_MSG);

    this.sessionId = sessionId;

    this.isTrackingEnabled = isTrackingEnabled;

    this.isInitialized = true;

    this.logger.debug(
      'Media Tracking Queue initialized with session id:',
      this.sessionId,
    );
    this.logger.debug('Tracking enabled:', this.isTrackingEnabled);
  }

  private logEventPushedToQueue(event: MediaTrackingEventName, cuid: string) {
    if (this.eventsToLog.length > 0 && !this.eventsToLog.includes(event)) {
      return;
    }

    this.logger.debug(
      `Event of type: "${event}" with cuid: "${cuid}" pushed to queue`,
    );
  }

  public push(eventData: MediaTrackingQueueEvent) {
    if (this.sessionId && this.isQueueEnabled()) {
      this.logEventPushedToQueue(eventData.event, eventData.cuid);

      this.eventsQueue.push({
        ...eventData,
        sessionId: this.sessionId,
      });

      if (this.eventsQueue.length >= this.maxEventsInQueue) {
        this.dispatchEvents();
      }

      if (!isDefined(this.timeoutId)) {
        this.timeoutId = setTimeout(() => {
          this.logger.debug('Timeout expired, dispatching events');
          this.dispatchEvents();
        }, this.timeoutLength);
      }
    } else {
      this.logger.debug('Tracking disabled, push skipped');
    }
  }

  public dispatchEvents = () => {
    if (this.eventsQueue.length > 0 && this.isQueueEnabled()) {
      this.logger.debug('Dispatching events:', this.eventsQueue);
      void this.trackEvents([...this.eventsQueue]);
      this.clearQueue();
    }
  };

  public debug = (...args: any[]) => {
    this.logger.debug(...args);
  };

  private isQueueEnabled() {
    return this.isInitialized && this.isTrackingEnabled;
  }

  private clearTimeout() {
    clearTimeout(this.timeoutId);
    this.timeoutId = undefined;
  }

  private clearQueue() {
    this.clearTimeout();
    this.eventsQueue = [];
  }
}

export const MediaTrackingQueue = new MediaEventTrackingQueue();
